import { groq } from "next-sanity";
import { redirectedBlogs } from "../../../constants/blog-visibility-config";
import { handleClientError } from "../../errors";
import { getClient } from "../sanity";

export type BlogAuthor = {
  name: string;
  imageUrl: string;
  bio: string;
  role: string;
  slug: string;
};

export type BlogCategory = {
  name: string;
  slug: string;
};

export type BlogSimple = {
  title: string;
  slug: string;
  imageUrl: string;
  imageAlt: string;
  excerpt: string;
  publishedAt: string;
  authors: BlogAuthor[];
  category: BlogCategory;
};

export type Blog = {
  content: string;
  relatedBlogs?: string[];
  metaTitle?: string;
  metaDescription?: string;
  metaImageUrl?: string;
  personaTag?: string;
  isLifecycleLeaders?: boolean;
  lifecycleLeadersRedirectBlog?: Blog;
} & BlogSimple;

export const blogSimpleFragment =
  "title, slug, excerpt, publishedAt, 'imageUrl': image.asset->url, imageAlt, authors[]->{name, slug}, category->{name, slug}, 'isLifecycleLeaders': coalesce(isLifecycleLeaders, false), lifecycleLeadersRedirectBlog->{title, slug}";

export const blogAuthorFragment = "name, slug, bio, 'imageUrl': picture.asset->url, role";

/**
 * Fetch featured blogs by slug. Optionally include hidden blogs.
 * @param slugs - The slugs of the blogs to fetch.
 * @param allowHidden - Whether to include hidden blogs in the results. Defaults to false.
 * @returns An array of `BlogSimple` objects.
 */
export const getFeaturedBlogs = async (slugs: string[], allowHidden = false): Promise<BlogSimple[]> => {
  // Build the query conditionally to avoid template literal interpolation
  let query = groq`*[_type == "blog"`;

  if (!allowHidden) {
    query += groq` && !hidden`;
  }

  query += groq` && slug in $slugs] {
    ${blogSimpleFragment}
  }`;

  try {
    // Use query parameters instead of string interpolation to avoid SQL injection
    const parameters = { slugs };
    const unorderedBlogs = await getClient().fetch(query, parameters);
    // Return related blogs in the selected order, and filter out any undefined values.
    return slugs.map((slug) => unorderedBlogs.find((blog: BlogSimple) => blog.slug === slug)).filter(Boolean);
  } catch (error) {
    const message = `Failed to fetch featured blogs: ${error instanceof Error ? error.message : String(error)}`;
    handleClientError(message);
    return [];
  }
};

const blogFragment = `
  ${blogSimpleFragment},
  content,
  'relatedBlogs': resources[]->slug,
  metaTitle,
  metaDescription,
  'metaImageUrl': metaImage.asset->url,
  'personaTag': persona->tag,
`;

export const getBlogPost = async (slug: string): Promise<Blog | undefined> => {
  const query = groq`
    *[_type == "blog" && slug == $slug][0] {
      ${blogFragment}
    }
  `;

  try {
    // Use query parameters instead of string interpolation to avoid SQL injection
    const parameters = { slug };
    const blogPost = await getClient().fetch(query, parameters);
    return blogPost;
  } catch (error) {
    const message = `Failed to fetch blog post with slug "${slug}": ${
      error instanceof Error ? error.message : String(error)
    }`;
    handleClientError(message);
    return undefined;
  }
};

export const getBlogCategories = async (): Promise<BlogCategory[]> => {
  const query = groq`
    *[_type == "blogCategory"] {
      name,
      slug,
      _updatedAt
    } | order(_updatedAt desc)
  `;

  try {
    const categories: BlogCategory[] = await getClient().fetch(query);
    return categories;
  } catch (error) {
    const message = `Failed to fetch blog categories: ${error instanceof Error ? error.message : String(error)}`;
    handleClientError(message);
    return [];
  }
};

export const blogsPerPageLimit = 40;
export const featuredBlogsCount = 1;

/** Fetch all unhidden, non-redirected blogs for a specific page. Optionally filtered by category or page.
 * @param categorySlug - The slug of the category to filter by.
 * @param page - The page number to fetch. Defaults to 1, not 0, since pages are user facing and start at 1.
 * @returns An array of `BlogSimple` objects.
 */
export const getBlogs = async (categorySlug: string, page = 1): Promise<BlogSimple[]> => {
  const offset = (page - 1) * blogsPerPageLimit;
  const limit = blogsPerPageLimit;

  let query = groq`*[_type == "blog" && !hidden && !(slug in $redirectedBlogs)`;

  if (categorySlug !== "all") {
    query += groq` && category->slug == $categorySlug`;
  }

  query += groq`] {
    ${blogSimpleFragment}
  } | order(publishedAt desc) [$offset...$limit]`;

  try {
    const parameters = {
      redirectedBlogs,
      categorySlug,
      offset,
      limit: offset + limit,
    };

    const blogs = await getClient().fetch(query, parameters);
    return blogs;
  } catch (error) {
    const message = `Failed to fetch blogs for category "${categorySlug}" page ${page}: ${
      error instanceof Error ? error.message : String(error)
    }`;
    handleClientError(message);
    return [];
  }
};

export const getBlogCount = async (categorySlug?: string): Promise<number> => {
  let query = groq`count(*[_type == "blog" && !hidden && !(slug in $redirectedBlogs)`;

  if (categorySlug && categorySlug !== "all") {
    query += groq` && category->slug == $categorySlug`;
  }

  query += groq`])`;

  try {
    const parameters = {
      redirectedBlogs,
      categorySlug,
    };

    const count = await getClient().fetch(query, parameters);
    return count;
  } catch (error) {
    const message = `Failed to fetch blog count${categorySlug ? ` for category "${categorySlug}"` : ""}: ${
      error instanceof Error ? error.message : String(error)
    }`;
    handleClientError(message);
    return 0;
  }
};

export const getAllAuthors = async (): Promise<BlogAuthor[]> => {
  const query = groq`
    *[_type == "blogAuthor" && defined(slug) && count(*[_type == "blog" && !hidden && !(slug in $redirectedBlogs) && ^._id in authors[]._ref]) > 0] | order(name asc) {
      name,
      slug, 
      'imageUrl': picture.asset->url, 
      role, 
    }
  `;

  try {
    const parameters = { redirectedBlogs };
    const authors = await getClient().fetch(query, parameters);
    return authors;
  } catch (error) {
    const message = `Failed to fetch blog authors: ${error instanceof Error ? error.message : String(error)}`;
    handleClientError(message);
    return [];
  }
};

export const getBlogAuthor = async (slug: string): Promise<BlogAuthor | undefined> => {
  const query = groq`
    *[_type == "blogAuthor" && slug == $slug][0] {
      ${blogAuthorFragment}
    }
  `;

  try {
    const parameters = { slug };
    const author = await getClient().fetch(query, parameters);
    return author;
  } catch (error) {
    const message = `Failed to fetch blog author with slug "${slug}": ${
      error instanceof Error ? error.message : String(error)
    }`;
    handleClientError(message);
    return undefined;
  }
};

export const getBlogsByAuthor = async (slug: string): Promise<BlogSimple[]> => {
  const query = groq`
    *[_type == "blog" && !hidden && !(slug in $redirectedBlogs) && $slug in authors[]->slug] {
      ${blogSimpleFragment}
    } | order(publishedAt desc)
  `;

  try {
    const parameters = { slug, redirectedBlogs };
    const blogs = await getClient().fetch(query, parameters);
    return blogs;
  } catch (error) {
    const message = `Failed to fetch blogs by author with slug "${slug}": ${
      error instanceof Error ? error.message : String(error)
    }`;
    handleClientError(message);
    return [];
  }
};
